// Per-project cycles cache. Loaded when a project opens and refreshed after
// any mutation. Mirrors the structure of store/statuses.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ApiCycle } from "@juuplan/shared";
import {
  listCycles,
  createCycle as apiCreateCycle,
  updateCycle as apiUpdateCycle,
  deleteCycle as apiDeleteCycle,
  type CycleCreate,
  type CycleUpdate,
} from "../lib/api";

export interface CyclesState {
  loading: boolean;
  items: ApiCycle[];
  projectId: number | null;
  error: string | null;
}

const initial: CyclesState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const cyclesSignal: Signal<CyclesState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadCycles(projectId: number): Promise<void> {
  try {
    const items = await listCycles(projectId);
    cyclesSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    cyclesSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the cycles for the given project.
 * Triggers a load when the projectId changes, and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useCycles(projectId: number): CyclesState {
  useEffect(() => {
    const s = cyclesSignal.value;
    if (!inFlight && (s.projectId !== projectId || s.error !== null)) {
      cyclesSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadCycles(projectId);
    }
  }, [projectId]);

  const state = cyclesSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshCycles(projectId: number): Promise<void> {
  cyclesSignal.value = { ...cyclesSignal.value, loading: true };
  inFlight = loadCycles(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/** Create a new cycle and refresh the cache. */
export async function addCycle(
  projectId: number,
  body: CycleCreate
): Promise<ApiCycle> {
  const created = await apiCreateCycle(projectId, body);
  await refreshCycles(projectId);
  return created;
}

/** Update a cycle and refresh the cache. */
export async function patchCycle(
  cycleId: number,
  projectId: number,
  body: CycleUpdate
): Promise<ApiCycle> {
  const updated = await apiUpdateCycle(cycleId, body);
  await refreshCycles(projectId);
  return updated;
}

/** Delete a cycle and remove it from the cache. */
export async function removeCycle(
  cycleId: number,
  projectId: number
): Promise<void> {
  await apiDeleteCycle(cycleId);
  const cur = cyclesSignal.value;
  if (cur.projectId === projectId) {
    cyclesSignal.value = {
      ...cur,
      items: cur.items.filter((c) => c.id !== cycleId),
    };
  }
}
